/**
 * PipelinesTable — the pipelines a team may submit batches to, one row
 * each, with where its latest batch got to. The latest batch and the
 * moving count come from the API's per-pipeline summary, not from a
 * capped list, so a pipeline with hundreds of batches reports the right
 * numbers.
 */

import { Link } from 'react-router-dom'

import type { TeamPipeline } from '@/api/generated/model'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatRelativeTime } from '@/lib/utils'

import { BatchStatusBadge } from './BatchStatusBadge'
import { pipelinePath } from './paths'

interface PipelinesTableProps {
  pipelines: TeamPipeline[]
}

export function PipelinesTable({ pipelines }: PipelinesTableProps): React.JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Pipeline</TableHead>
          <TableHead className="hidden md:table-cell">Stages</TableHead>
          <TableHead>Latest batch</TableHead>
          <TableHead className="text-right">Active</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pipelines.map((pipeline) => {
          const latest = pipeline.latest_batch
          return (
            <TableRow key={pipeline.name}>
              <TableCell className="font-medium">
                <Link to={pipelinePath(pipeline.name)} className="text-foreground hover:underline">
                  {pipeline.display_name}
                </Link>
                <div className="font-mono text-xs text-muted-foreground">{pipeline.name}</div>
              </TableCell>
              <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">
                {pipeline.stages.join(' · ')}
              </TableCell>
              <TableCell>
                {latest === null ? (
                  <span className="text-sm text-muted-foreground">No batches yet</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <BatchStatusBadge status={latest.status} />
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(latest.created_at)}
                    </span>
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right font-mono text-xs tabular-nums">
                {pipeline.active_batches.toString()}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
